import {
    AttachedFiles,
    type AttachedFilesRequest,
    BTFileSystem,
    type IBTFileSystemData,
    type IHasAttachedFiles,
} from "legacyComponents/FileUploadContainer.types";
import {
    type ILineItemContainerData,
    type ILineItemData,
    type ILineItemDefaults,
    type ILineItemResponse,
    LineItem,
    LineItemType,
    PriceTypes,
} from "legacyComponents/LineItemContainer.types";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

import type { ApplicableDeposit as ApplicableDepositResponse } from "@btnet/services-btnet/handlers/models/applicableDeposit";
import type { GetInvoiceResponse } from "@btnet/services-btnet/handlers/models/getInvoiceResponse";
import type { Int32BTServiceListItemIEnumerableBTServiceValidatedField } from "@btnet/services-btnet/handlers/models/int32BTServiceListItemIEnumerableBTServiceValidatedField";
import type { IntegrationTypes } from "@btnet/services-btnet/handlers/models/integrationTypes";
import { InvoiceFormat } from "@btnet/services-btnet/handlers/models/invoiceFormat";
import type { InvoiceLineItem } from "@btnet/services-btnet/handlers/models/invoiceLineItem";
import type { InvoiceLineItemMetadata } from "@btnet/services-btnet/handlers/models/invoiceLineItemMetadata";
import type { InvoicePaymentDetailsResponse } from "@btnet/services-btnet/handlers/models/invoicePaymentDetailsResponse";
import type { LineItemGroupStrategy } from "@btnet/services-btnet/handlers/models/lineItemGroupStrategy";
import type { OwnerInvoiceClientPreview } from "@btnet/services-btnet/handlers/models/ownerInvoiceClientPreview";
import type { OwnerInvoicePreviewJobsite } from "@btnet/services-btnet/handlers/models/ownerInvoicePreviewJobsite";
import type { UserOrgLink } from "@btnet/services-btnet/handlers/models/userOrgLink";

import {
    BTSelectItem,
    type IBaseEntity,
    type IWithServiceValidationErrors,
    mapBTServiceDropdownToSelectItem,
    type ServiceValidation,
} from "types/apiResponse/apiResponse";
import {
    AccountingIntegrationType,
    type ActivationStatus,
    CustomFieldAssociatedType,
    DocumentInstanceType,
    type JobContractType,
    PaymentEntityTypes,
    PaymentType,
} from "types/enum";

import { getTaxGroupSelectItemsFromLineItemDefaults } from "utilities/lineItem/lineItemHelper";

import {
    type AccountingValidationData,
    type IHasAccountingValidation,
} from "commonComponents/accounting/AccountingValidation/AccountingValidation.types";
import {
    ContactOrgLink,
    type IContactEmailAndInviteFormValues,
} from "commonComponents/entity/contact/ContactEmailAndInvite/ContactEmailAndInvite/ContactEmailAndInvite.api.types";
import { type ICustomFieldFormValues } from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer";
import {
    CustomFieldPrintItem,
    type ICustomFieldOptionsList,
    type ICustomFieldResponse,
    type IHasCustomFieldContainer,
} from "commonComponents/entity/customField/CustomFieldContainer/CustomFieldContainer.types";
import {
    DiscussionsEntity,
    type IHasDiscussionContainer,
} from "commonComponents/entity/discussion/DiscussionContainer/DiscussionContainer.api.types";
import { Email } from "commonComponents/entity/email/InputEmail/InputEmail.types";
import {
    type ILineItemsToInvoiceDetails,
    PaymentStatusType,
} from "commonComponents/entity/invoicing/LineItemsToInvoice/LineItemsToInvoice.api.types";
import { getUniqueParents } from "commonComponents/entity/invoicing/LineItemsToInvoice/LineItemsToInvoice.utils";
import {
    type IHasLinkedScheduleItem,
    LinkToScheduleItemEntity,
    type LinkToScheduleItemFormValues,
} from "commonComponents/entity/linkToScheduleItem/LinkToScheduleItem/LinkToScheduleItem.api.types";
import { MerchantPaymentListData } from "commonComponents/entity/merchantPayment/MerchantPaymentList/MerchantPaymentList";
import {
    type IRecentItemsTooltipData,
    RecentItemsTooltipConfig,
} from "commonComponents/entity/recentItemsTooltip/RecentItemsTooltip";
import {
    AccountingEntityTypes,
    AccountingValidationResponse,
} from "commonComponents/financial/Common/Accounting.types";
import { type TaxGroupServiceListItemExtraData } from "commonComponents/financial/TaxRateSelect/TaxRateSelect.api.types";
import { PrintHeaderAPI } from "commonComponents/print/PrintHeader/PrintHeader.types";

import { CreditMemoAppliedInvoice } from "entity/creditMemo/CreditMemo/CreditMemo.api.types";
import { FilesFromExternalUrlsResponse } from "entity/document/Document/Document.api.types";
import { type IHasOnlinePayments } from "entity/onlinePayment/OnlinePayment.api.types";
import { InvoiceStatus } from "entity/ownerInvoice/common/OwnerInvoiceStatus/OwnerInvoiceStatus";
import {
    type IOwnerInvoiceLineItem,
    OwnerInvoiceLineItem,
} from "entity/ownerInvoice/OwnerInvoiceLineItemContainer/OwnerInvoiceLineItemContainer.types";
import { type UnverfiedPaymentMethodResponse } from "entity/OwnerSummary/OwnerSummary.api.types";
import { mapDeadlineToScheduleLinkItem } from "entity/pdfSignature/PdfSignatureRequest.utilities";
import { JobTaxVersion, type TaxMethod } from "entity/tax/common/tax.types";
import { TaxRateBreakdownInfo } from "entity/tax/common/TaxRateBreakdown/TaxRateBreakdown.api.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";

import type { default as OwnerInvoiceApiResponse } from "./OwnerInvoice.api.json";

export interface IReconciliationLocationState {
    reconciliationLineItems?: InvoiceLineItem[];
    // Wizard output for a grid-launched invoice, via location state so it survives first render
    // (React 17 batching) and refresh.
    lineItemsToInvoice?: ILineItemsToInvoiceDetails;
}

export interface IOwnerInvoiceLinkResponse {
    ownerInvoiceId: number;
    invoiceUrl: string;
}
export enum OwnerInvoiceLineItemColumn {
    Items = 0,
    CostType = 1,
    UnitCost = 2,
    Quantity = 3,
    Markup = 4,
    MarkupAmount = 5,
    Tax = 6,
    OwnerPrice = 7,
    Description = 8,
    UnitPrice = 9,
    BuilderCost = 10,
    MarkedAs = 11,
    RelatedItems = 12,
    Files = 13,
}

export type OwnerInvoiceFormActions =
    | undefined
    | "save"
    | "saveAndRelease"
    | "saveAndInvoice"
    | "saveAndClose"
    | "reset"
    | "unrelease"
    | "delete"
    | "void"
    | "resend"
    | "print"
    | "addOwnerEmail"
    | "skipAddingOwnerEmail"
    | "saveDefaults"
    | "sendToAccounting"
    | "scheduleRelease";

export interface IOwnerInvoiceFormValues {
    title: string;
    customInvoiceId: string;
    description: string;
    closingText: string;
    paidDate?: moment.Moment;
    status: InvoiceStatus;
    amountPaid: number;
    ownerEmail: string;
    createInvoiceChkbox: boolean;
    notifyOwner: boolean;
    customFields: ICustomFieldFormValues[];
    attachedFiles: AttachedFilesRequest;
    files?: BTFileSystem[];
    showLineItemsToOwner: boolean;
    showPaymentCode: boolean;
    showCustomFields: boolean;
    showCostCodes: boolean;
    showCategories: boolean;
    showContractorCertification: boolean;
    showArchitectCertification: boolean;
    showRetainage: boolean;
    showStoredMaterials: boolean;
    showItems: boolean;
    showInvoiceDescription: boolean;
    lineItems: IInvoiceLineItemWithMetadata[];
    costCodeIds: number[];
    builderCost: string;
    unifiedDeadlineRequest: LinkToScheduleItemFormValues;
    internalNotes: string;
    priceType: PriceTypes;
    containerIsValid: boolean;
    ownerInvoiceLineItems: IOwnerInvoiceLineItem[];
    amount: number;
    taxMethod: TaxMethod;
    taxGroupId?: number;
    contactSaveRequest?: IContactEmailAndInviteFormValues;
    columnPreferences: OwnerInvoiceLineItemColumn[];
    groupLineItemsByCostCode: boolean;
    scheduledReleaseDate?: Date;
    invoiceDate?: Date;
    invoiceFormat: InvoiceFormat;
    lineItemGroupStrategy?: LineItemGroupStrategy;
    hideLaborCostAndMarkup: boolean;
    hideBuilderMarkupFromSubtotal: boolean;
    hideCostCodes: boolean;
    hideTaxAgencyDetail: boolean;
}

export interface IOwnerOwnerInvoiceFormValues {
    priceType: PriceTypes;
    ownerInvoiceLineItems: IOwnerInvoiceLineItem[];
    amount: number;
    showLineItemsToOwner: boolean;
    showPaymentCode?: boolean;
    showCustomFields?: boolean;
    showInvoiceDescription?: boolean;
    taxMethod: TaxMethod;
    taxGroupId?: number;
    groupLineItemsByCostCode?: boolean;
}

export interface IContactOrgLink {
    orgLinkId?: number;
    contactId?: number;
    activationStatus: ActivationStatus;
    displayName: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneCell?: string;
}

export interface IOwnerInvoiceOwnerPreviewDisplayPreferences {
    /** Undefined leaves the stored selection alone; the endpoint only rewrites what it's sent. */
    columnPreferences: OwnerInvoiceLineItemColumn[] | undefined;
    saveColumnPreferencesAsDefault: boolean;
    showLineItems: boolean | undefined;
    showPaymentCode: boolean | undefined;
    showCustomFields: boolean | undefined;
    showInvoiceDescription: boolean | undefined;
    groupLineItemsByCostCode: boolean | undefined;
    hideLaborCostAndMarkup: boolean | undefined;
    hideBuilderMarkupFromSubtotal: boolean | undefined;
    hideCostCodes: boolean | undefined;
    hideTaxAgencyDetail: boolean | undefined;
}

export interface ISaveOwnerInvoiceOwnerPreviewDisplayPreferencesRequest {
    columnPreferences: OwnerInvoiceLineItemColumn[] | undefined;
    saveAsDefault: boolean;
    hideLineItems: boolean | undefined;
    showPaymentCode: boolean | undefined;
    showCustomFields: boolean | undefined;
    showInvoiceDescription: boolean | undefined;
    groupLineItemsByCostCode: boolean | undefined;
    hideLaborCostAndMarkup: boolean | undefined;
    hideBuilderMarkupFromSubtotal: boolean | undefined;
    hideCostCodes: boolean | undefined;
    hideTaxAgencyDetail: boolean | undefined;
}

export enum InvoicePaymentMethods {
    Other = -1,
    CreditCard = 0,
    Check = 1,
    OnlinePayment = 2,
    QuickBooks = 3,
    Xero = 4,
    CreditMemo = 5,
    Deposit = 6,
    Wire = 7,
    SageIntacct = 8,
}
export interface ILineItemResponseWithMetaData extends ILineItemResponse {
    invoiceLineItemMetadata?: InvoiceLineItemMetadata;
    retainageWithheld?: number | null;
    storedMaterials?: number;
}

export interface IInvoiceLineItemWithMetadata extends LineItem {
    invoiceLineItemMetadata?: InvoiceLineItemMetadata;
    retainageWithheld?: number | null;
    storedMaterials?: number;
}

export class InvoiceLineItemWithMetadata extends LineItem implements IInvoiceLineItemWithMetadata {
    constructor(data: ILineItemResponseWithMetaData, pageType: string, pageTypeEnum: LineItemType) {
        super(data, pageType, pageTypeEnum);
        this.invoiceLineItemMetadata = data.invoiceLineItemMetadata;
        this.retainageWithheld = data.retainageWithheld;
        this.storedMaterials = data.storedMaterials;
    }

    invoiceLineItemMetadata?: InvoiceLineItemMetadata;
    retainageWithheld?: number | null;
    storedMaterials?: number;
}

export interface IInvoiceLineItemData extends ILineItemData {
    value: ILineItemResponseWithMetaData[];
}

export interface IOwnerInvoiceEntityInterface
    extends IBaseEntity,
        IHasLinkedScheduleItem,
        IHasAttachedFiles,
        IHasDiscussionContainer,
        IHasCustomFieldContainer,
        IHasAccountingValidation,
        IHasOnlinePayments {
    id: number;
    jobId: number;
    billRateEnabledForJob: boolean;
    builderId: number;
    externalId?: string;
    externalBuilderId?: string;
    externalJobId?: string;
    instanceKey: string;
    jobContractType?: JobContractType;
    invoiceFormat: InvoiceFormat;
    isSelectionsReimaginedJob?: boolean;
    hideLaborCostAndMarkup: boolean;
    hideBuilderMarkupFromSubtotal: boolean;
    hideCostCodes: boolean;
    hideTaxAgencyDetail: boolean;

    jobName: string;
    customInvoiceId: string;
    invoiceJobPrefixes?: string;
    title: string;
    recentItemsTooltip: RecentItemsTooltipConfig;
    merchantPayments?: MerchantPaymentListData;
    invoicePayments?: InvoicePayment[];

    amountPaid: number;
    balance: number;
    paidDate?: moment.Moment;
    amount: number;

    description: string;
    closingText: string;
    internalNotes: string;
    addedBy: string;
    status: InvoiceStatus;
    releasedDate?: moment.Moment;
    releasedByName?: string;
    scheduledReleaseDate?: moment.Moment;

    createdByDate: moment.Moment;
    createdByName: string;
    createdById: number | null;
    lastUpdatedByDate: moment.Moment;
    lastUpdatedByName: string;
    lastUpdatedById: number | null;
    viewedByOwnerOn: moment.Moment;

    isTemplate: boolean;
    canAdd: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canVoid: boolean;
    hasNewerProgressInvoice: boolean;
    canRefund: boolean;
    canPartiallyRefund: boolean;
    canPayOtherAmount: boolean;
    showRefund: boolean;
    showDeleteMessage: boolean;
    shouldRequestOwnerEmail: boolean;
    shouldPromptNotifyOwner: boolean;
    canOwnerViewInvoicesTab: boolean;
    ownerEmail: Email;

    isConnectedToAccounting: boolean;
    isConnectedAndInvoicedToAccounting: boolean;
    isJobAccountingConnected: boolean;
    isBilled?: boolean;
    showPayOnlineBtn: boolean;
    showPushPaymentBtn: boolean;
    showTerminalPaymentsEnabled: boolean;
    payOnlineUrl: string;
    payOnlineMessage: string;
    addFromRelatedEntities: AddFromRelatedEntitiesResponse;
    paymentLimitAmount: number | null;
    maxTransactionAmount: number;
    // IHasLinkedScheduleItem
    linkToScheduleItemEntity: LinkToScheduleItemEntity;

    // IHasAttachedFiles
    attachedFiles: AttachedFiles;
    attachedFilesForRelatedEntity: FilesFromExternalUrlsResponse | BTFileSystem[];
    /** The bills a client of the job would be linked to; the builder's client preview shows only these. */
    clientBillLinkIds: number[];
    /** Whether the job shares Purchase Orders/Bills with clients; undefined when the server didn't say. */
    jobSharesBillsWithClients?: boolean;
    documentInstanceType: DocumentInstanceType;
    name: string;

    // IHasDiscussionContainer
    discussions: DiscussionsEntity;

    // IHasCustomFields
    customFields: ICustomFieldResponse[];
    customFieldAssociatedType: CustomFieldAssociatedType;
    customFieldOptions: ICustomFieldOptionsList;
    customFieldsCanConfigure: boolean;

    // IHasLineItems
    lineItems?: IInvoiceLineItemData;
    costCategoryOptions?: Int32BTServiceListItemIEnumerableBTServiceValidatedField;
    lineItemContainerData: ILineItemContainerData;
    lineItemInitialValues: IInvoiceLineItemWithMetadata[];
    showLineItemsToOwner: boolean;
    groupLineItemsByCostCode: boolean;
    showRelatedStackItems: boolean;
    ownerInvoiceLineItems: IOwnerInvoiceLineItem[];

    retainageProductServiceId: string | null;

    // IHasAccountingValidation
    accountingValidation: AccountingValidationData;
    accountingSectionTitle: string;
    invoiceAccountingLinkInformation?: IInvoiceAccountingLinkInformation;
    isLinkedToAccountingService: boolean;
    isAccountingSyncLocked: boolean;
    removeFromAccountingMessage?: string;
    resetToPendingCustomMessage: string | undefined;
    entityType: PaymentEntityTypes;

    shouldSendToAccounting: boolean | undefined;
    customTitle: string;

    totalTaxAmount?: number;
    accountingBalance?: number;
    accountingIntegrationName?: string;

    // Tax Fields
    taxMethod: BTSelectItem[] | undefined;
    taxGroupId?: number;
    jobDefaultTaxGroupId?: number | null;
    taxRateBreakdown?: TaxRateBreakdownInfo;
    totalWithTax?: number;

    hasCostCodesFeature: boolean;

    accountingIntegrationType?: AccountingIntegrationType;

    // Contact Information
    payeeList: IContactOrgLink[];
    hasBeenSentToOwner: boolean;

    encodedEntityId: string;
    taxAmount?: number;
    totalInvoiceAmountWithTaxes?: number;
    showDeadlineTime: boolean;
    deadLine?: moment.Moment;
    showInviteOnlinePaymentsButton: boolean;
    taxGroups?: BTSelectItem<TaxGroupServiceListItemExtraData>[];
    canViewAllInvoicesOnJob: boolean;
    applicableDeposits: ApplicableDeposit[] | null;

    columnPreferences: OwnerInvoiceLineItemColumn[];
    defaultCostPlusOwnerInvoiceColumns?: OwnerInvoiceLineItemColumn[];
    defaultGeneralOwnerInvoiceColumns?: OwnerInvoiceLineItemColumn[];
    defaultGeneralHideOwnerLineItems?: boolean;
    defaultGeneralGroupLineItemsByCostCode?: boolean;
    defaultCostPlusHideOwnerLineItems?: boolean;
    defaultCostPlusGroupLineItemsByCostCode?: boolean;
    defaultCostPlusHideLaborCostAndMarkup?: boolean;
    defaultCostPlusHideBuilderMarkupFromSubtotal?: boolean;
    defaultGeneralHideCostCodes?: boolean;
    defaultCostPlusHideCostCodes?: boolean;
    defaultGeneralHideTaxAgencyDetail?: boolean;
    defaultCostPlusHideTaxAgencyDetail?: boolean;
    showPaymentCode: boolean;
    showCustomFields: boolean;
    showCostCodes: boolean;
    showCategories: boolean;
    showContractorCertification: boolean;
    showArchitectCertification: boolean;
    showRetainage: boolean;
    showStoredMaterials: boolean;
    showItems: boolean;
    showInvoiceDescription: boolean;

    builderHasLendingEnabled: boolean;
    jobHasLoanPaymentMethod: boolean;
    loanHasPendingDrawRequest: boolean;
    isConnectedToInboundPayments: boolean;
    isInboundPaymentsEnabledOnJob: boolean;
    disableApplyADepositButton: boolean;
    invoiceDate?: moment.Moment;
    getStatus: () => "unreleased" | null;
    effectiveTaxVersion?: JobTaxVersion;
    lineItemGroupStrategy?: LineItemGroupStrategy;
    estimateHasAllowances?: boolean;
    printHeader?: PrintHeaderAPI;
    previewJobsite?: OwnerInvoicePreviewJobsite;
    qrCodeUrl: string | null;
    paymentCode: string | null;
    showPaymentFromPrintoutInformation: boolean;
    canShowPaymentFromPrintoutInformation: boolean;
    showFees: boolean;
    showMerchantPayments: boolean;
    hasCustomFields: boolean;
    customFieldsPrint: CustomFieldPrintItem[];
    originalContractSum: number;
    netChangeOrderTotal: number;
}

export interface IInvoiceAccountingLinkInformation {
    isInvoiceLinked: boolean;
    lastSyncDateUtc: moment.Moment | null;
    integrationType: IntegrationTypes | undefined;
    isInSyncingStatus: boolean;
}

export interface IPaymentAccountingLinkInformation {
    isPaymentLinked: boolean;
    lastSyncDateUtc: moment.Moment | null;
    integrationType: IntegrationTypes | undefined;
}

export class OwnerInvoiceEntity implements IOwnerInvoiceEntityInterface {
    constructor(data: {
        lineItemGroupStrategy: LineItemGroupStrategy;
        estimateHasAllowances?: boolean;
        isSelectionsReimaginedJob?: boolean;
        retainageProductServiceId: string | null;
        jobDefaultTaxGroupId?: number | null;
        hasNewerProgressInvoice: boolean;
        clientBillLinkIds?: GetInvoiceResponse["clientBillLinkIds"];
        jobSharesBillsWithClients?: GetInvoiceResponse["jobSharesBillsWithClients"];
        [key: string]: any;
    }) {
        this.name = "Invoice";
        this.documentInstanceType = DocumentInstanceType.CustomerInvoices;
        this.customFieldAssociatedType = CustomFieldAssociatedType.OwnerPayments;

        this.id = data.id || 0;
        this.jobId = data.jobId;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        this.billRateEnabledForJob = data.billRateEnabledForJob ?? false;
        this.builderId = data.builderId;
        this.externalId = (data.externalId as string) ?? undefined;
        this.externalBuilderId = (data.externalBuilderId as string) ?? undefined;
        this.externalJobId = (data.externalJobId as string) ?? undefined;
        this.jobName = data.jobName?.value;
        this.instanceKey = uuidv4();
        this.customInvoiceId = data.customInvoiceId.value;
        this.invoiceJobPrefixes = data.invoiceJobPrefixes;
        this.title = data.title.value;
        if (data.recentItemsTooltip) {
            this.recentItemsTooltip = new RecentItemsTooltipConfig(
                data.recentItemsTooltip as IRecentItemsTooltipData,
            );
        }
        this.merchantPayments = data.merchantPayments
            ? new MerchantPaymentListData(data.merchantPayments)
            : undefined;
        this.invoicePayments = data.invoicePayments?.map(
            (payment: any) => new InvoicePayment(payment),
        );
        this.amountPaid = data.amountPaid.value;
        this.balance = data.balance.value;
        this.amount = data.amount.value;
        this.canPayOtherAmount = data.canEditPaymentAmount;

        if (data.paidDate.value) {
            this.paidDate = moment(data.paidDate.value);
        }
        this.description = data.description.value;
        this.closingText = (data.closingText as { value?: string } | undefined)?.value ?? "";
        this.internalNotes = data.internalNotes;
        this.addedBy = data.addedBy ? data.addedBy.value : undefined;
        this.status = data.status.value;

        this.createdByDate = moment(data.createdByDate);
        this.createdByName = data.createdByName;
        this.createdById = data.createdById;
        this.lastUpdatedByDate = moment(data.lastUpdatedByDate);
        this.lastUpdatedByName = data.lastUpdatedByName;
        this.lastUpdatedById = data.lastUpdatedById;

        this.isTemplate = data.isTemplate;
        this.canAdd = data.canAdd;
        this.canEdit = data.canEdit || this.id === 0;
        this.canDelete = data.canDelete;
        this.canVoid = data.canVoid;
        this.hasNewerProgressInvoice = data.hasNewerProgressInvoice;
        this.canRefund = data.canRefund;
        this.canPartiallyRefund = data.canPartiallyRefund;
        this.canPayOtherAmount = data.canEditPaymentAmount;
        this.showRefund = data.showRefund;
        this.showDeleteMessage = !!data.deleteMessage;
        this.shouldRequestOwnerEmail = data.shouldRequestOwnerEmail;
        // presence of this key on response indicates should prompt to notify
        this.shouldPromptNotifyOwner = data.showNotifyOwnerChkbox !== undefined;
        this.canOwnerViewInvoicesTab = data.canOwnerViewInvoicesTab;
        if (data.jobInfo) {
            this.ownerEmail = new Email(data.jobInfo.ownerEmail);
            this.jobContractType = data.jobInfo.contractType;
        }
        this.isSelectionsReimaginedJob = data.isSelectionsReimaginedJob;
        this.isConnectedToAccounting = data.isConnectedToAccounting;
        this.isConnectedAndInvoicedToAccounting = data.isConnectedAndInvoicedToAccounting;
        this.isJobAccountingConnected = data.isJobAccountingConnected ?? false;
        this.isBilled = data.isBilled;

        // AccountingService invoices flow through InvoiceEntityNew, so the legacy entity is never sync-locked.
        this.isLinkedToAccountingService = false;
        this.isAccountingSyncLocked = false;

        this.showPayOnlineBtn = data.showPayOnlineBtn;
        this.showPushPaymentBtn = data.showPushPaymentBtn;
        this.showTerminalPaymentsEnabled = data.terminalPaymentsEnabled;
        this.payOnlineUrl = data.payOnlineUrl;
        this.linkToScheduleItemEntity = new LinkToScheduleItemEntity(data);
        this.attachedFiles = new AttachedFiles(data.attachedFiles);
        if (data.attachedFilesForRelatedEntity) {
            if (this.id === 0) {
                this.attachedFilesForRelatedEntity = new FilesFromExternalUrlsResponse(
                    data.attachedFilesForRelatedEntity,
                );
            } else {
                this.attachedFilesForRelatedEntity = data.attachedFilesForRelatedEntity.map(
                    (file: IBTFileSystemData) => new BTFileSystem(file),
                );
            }
        }
        this.clientBillLinkIds = data.clientBillLinkIds ?? [];
        this.jobSharesBillsWithClients = data.jobSharesBillsWithClients ?? undefined;
        this.paymentLimitAmount = data.paymentLimitAmount;
        this.maxTransactionAmount = data.maxTransactionAmount;
        this.discussions = new DiscussionsEntity(data.discussions);

        this.customFields = data.customFields;
        this.customFieldOptions = data.customFieldOptions;
        this.customFieldsCanConfigure = data.customFieldsCanConfigure;
        this.showLineItemsToOwner = data.showLineItemsToOwner?.value;
        this.showRelatedStackItems = data.showRelatedStackItems;
        if (data.lineItems) {
            this.lineItems = {
                ...data.lineItems,
                value: data.lineItems.value.map((x: any) => {
                    // bt-line-item requires calculatedAmount to be populated
                    return { ...x, calculatedAmount: x.builderCost };
                }),
            };
        }

        this.lineItemContainerData = {
            entityId: this.id,
            pageType: "invoice",
            pageTypeEnum: LineItemType.OwnerInvoice,
            priceType: data.useLineItems?.value ? PriceTypes.LineItems : PriceTypes.FlatFee,
            showToOwner: this.showLineItemsToOwner,
            showMarkupTotal: false,
            displayTitle: true,
            descriptionMax: 2500,
            internalNotesMax: 2500,
            hasEditPermission: this.canEdit,
            customUnitCostHeader: "Unit Price",
            customBuilderCostHeader: "Client Price",
            hideFlatFeeOwnerPrice: true,
            showToOwnerTooltipVisible: false,
            disableBuilderCost:
                this.isBilled ||
                this.isAccountingSyncLocked ||
                !this.canEdit ||
                this.status === InvoiceStatus.Void,
            descriptionReadOnly: this.isBilled || this.isAccountingSyncLocked,
            lineItemReadOnly:
                this.isBilled ||
                this.isAccountingSyncLocked ||
                !this.canEdit ||
                this.status === InvoiceStatus.Void,
            canReorderLineItems: true,
            canEditCostCodes: data.canEditCostCodes,
        };

        if (data.lineItems?.value) {
            this.lineItemInitialValues = data.lineItems.value.map(
                (x: ILineItemResponseWithMetaData) => {
                    const data = new InvoiceLineItemWithMetadata(
                        { ...x },
                        this.lineItemContainerData.pageType,
                        this.lineItemContainerData.pageTypeEnum,
                    );

                    return data;
                },
            );
        } else {
            this.lineItemInitialValues = [];
        }

        this.ownerInvoiceLineItems =
            data.lineItems?.value.map((x: ILineItemResponse) => {
                return new OwnerInvoiceLineItem({ ...x });
            }) ?? [];
        if (
            data.invoiceFields &&
            data.invoiceFields.resetInvoice &&
            data.invoiceFields.resetInvoice.actionData &&
            data.invoiceFields.resetInvoice.actionData.resetMessage
        ) {
            this.removeFromAccountingMessage =
                data.invoiceFields.resetInvoice.actionData.resetMessage;
        }

        this.retainageProductServiceId = data.retainageProductServiceId ?? null;

        this.accountingValidation = {
            entityId: this.id,
            entityType: AccountingEntityTypes.OwnerInvoice,
            showValidation: false,
            showAccountsReceivable: data.showAccountsReceivable,
            canGetLatestStatus: data.canGetLatestStatus,
            canResetAccounting:
                this.removeFromAccountingMessage !== undefined &&
                this.removeFromAccountingMessage !== "",
            canSendToAccounting: data.createInvoiceChkbox || data.createInvoiceButton,
            shouldValidateOnLoad: true,
            accountingIntegrationName: data.accountingSectionTitle,
            transactionsAlertMessage:
                data?.invoiceFields?.resetInvoice?.actionData?.transactionsAlertMessage,
        };
        this.shouldSendToAccounting =
            this.canOwnerViewInvoicesTab &&
            (this.status === InvoiceStatus.Unreleased || this.status === InvoiceStatus.Scheduled) &&
            data.createInvoiceChkbox &&
            data.createInvoiceChkbox.value;
        this.accountingSectionTitle = data.accountingSectionTitle;

        if (
            this.status === InvoiceStatus.Void &&
            data.status.actions &&
            data.status.actions.find((x: any) => x.id === PaymentStatusType.PendingReleased)
        ) {
            this.resetToPendingCustomMessage = data.status.actions.find(
                (x: any) => x.id === PaymentStatusType.PendingReleased,
            ).message;
        }
        this.entityType = PaymentEntityTypes.OwnerInvoice;
        this.payOnlineMessage = data.payOnlineMessage;
        this.customTitle = `Invoice ID ${this.customInvoiceId}: ${this.title}`;

        this.totalTaxAmount = data.totalTaxAmount && data.totalTaxAmount.value;
        this.accountingBalance = data.accountingBalance && data.accountingBalance.value;
        this.accountingIntegrationName = data.accountingIntegrationName;
        this.addFromRelatedEntities = data.addFromRelatedEntities;

        this.taxMethod = data.taxMethod && mapBTServiceDropdownToSelectItem(data.taxMethod);
        this.taxGroupId = data.taxGroupId ?? NoTaxId;
        this.jobDefaultTaxGroupId = data.jobDefaultTaxGroupId ?? null;
        this.taxRateBreakdown =
            data.taxRateBreakdown && new TaxRateBreakdownInfo(data.taxRateBreakdown);
        this.totalWithTax = data.totalWithTax?.value;
        this.hasCostCodesFeature = data.hasCostCodesFeature;

        this.accountingIntegrationType = data.accountingType;
        this.payeeList = data.payeeList?.map((x: IContactOrgLink) => new ContactOrgLink(x)) ?? [];
        this.hasBeenSentToOwner = data.hasBeenSentToOwner;

        this.encodedEntityId = data.encodedEntityId;
        this.taxAmount = data.taxAmount;
        this.totalInvoiceAmountWithTaxes = data.totalInvoiceAmountWithTaxes;
        this.showDeadlineTime = data.dueTime?.value !== undefined && data.dueTime?.value !== null;
        if (data.dueTime?.value) {
            this.deadLine = moment(data.dueTime.value);
        } else if (data.deadLine?.value) {
            this.deadLine = moment(data.deadLine.value);
        }

        this.showInviteOnlinePaymentsButton = data.showInviteOnlinePaymentsButton !== undefined;
        this.taxGroups =
            data.lineItems?.defaults &&
            getTaxGroupSelectItemsFromLineItemDefaults(data.lineItems.defaults);

        this.canViewAllInvoicesOnJob = data.canViewAllInvoicesOnJob ?? false;
        this.applicableDeposits = data.applicableDeposits?.map(
            (x: (typeof OwnerInvoiceApiResponse.applicableDeposits)[0]) => new ApplicableDeposit(x),
        );
        if (data.releasedDate) {
            this.releasedDate = moment(data.releasedDate);
        }
        this.releasedByName = data.releasedByName;

        if (data.scheduledReleaseDate) {
            this.scheduledReleaseDate = moment(data.scheduledReleaseDate);
        }

        this.columnPreferences = data.ownerInvoiceLineItemColumnPreferences ?? [];
        this.defaultGeneralOwnerInvoiceColumns = data.defaultGeneralOwnerInvoiceColumns;
        this.defaultCostPlusOwnerInvoiceColumns = data.defaultCostPlusOwnerInvoiceColumns;
        this.defaultGeneralHideOwnerLineItems = data.defaultGeneralHideOwnerLineItems;
        this.defaultCostPlusHideOwnerLineItems = data.defaultCostPlusHideOwnerLineItems;
        this.defaultGeneralGroupLineItemsByCostCode = data.defaultGeneralGroupLineItemsByCostCode;
        this.defaultCostPlusGroupLineItemsByCostCode = data.defaultCostPlusGroupLineItemsByCostCode;
        this.defaultCostPlusHideLaborCostAndMarkup = (
            data as { defaultCostPlusHideLaborCostAndMarkup?: boolean }
        ).defaultCostPlusHideLaborCostAndMarkup;
        this.defaultCostPlusHideBuilderMarkupFromSubtotal =
            (data as { defaultCostPlusHideBuilderMarkupFromSubtotal?: boolean })
                .defaultCostPlusHideBuilderMarkupFromSubtotal ?? false;
        const hideCostCodesDefaults = data as {
            defaultGeneralHideCostCodes?: boolean;
            defaultCostPlusHideCostCodes?: boolean;
        };
        this.defaultGeneralHideCostCodes =
            hideCostCodesDefaults.defaultGeneralHideCostCodes ?? false;
        this.defaultCostPlusHideCostCodes =
            hideCostCodesDefaults.defaultCostPlusHideCostCodes ?? false;
        const hideTaxAgencyDetailDefaults = data as {
            defaultGeneralHideTaxAgencyDetail?: boolean;
            defaultCostPlusHideTaxAgencyDetail?: boolean;
        };
        this.defaultGeneralHideTaxAgencyDetail =
            hideTaxAgencyDetailDefaults.defaultGeneralHideTaxAgencyDetail ?? false;
        this.defaultCostPlusHideTaxAgencyDetail =
            hideTaxAgencyDetailDefaults.defaultCostPlusHideTaxAgencyDetail ?? false;
        this.showPaymentCode = data.customerInvoiceInformation?.showPaymentCode ?? false;
        this.showCustomFields = data.customerInvoiceInformation?.showCustomFields ?? false;
        this.showCostCodes =
            (data as { clientPreview?: OwnerInvoiceClientPreview }).clientPreview?.showCostCodes ??
            true;
        this.showCategories =
            (data as { clientPreview?: OwnerInvoiceClientPreview }).clientPreview?.showCategories ??
            true;
        this.showContractorCertification =
            (data as { clientPreview?: OwnerInvoiceClientPreview }).clientPreview
                ?.showContractorCertification ?? true;
        this.showArchitectCertification =
            (data as { clientPreview?: OwnerInvoiceClientPreview }).clientPreview
                ?.showArchitectCertification ?? true;
        this.showRetainage =
            (data as { clientPreview?: OwnerInvoiceClientPreview }).clientPreview?.showRetainage ??
            true;
        this.showStoredMaterials =
            (data as { clientPreview?: OwnerInvoiceClientPreview }).clientPreview
                ?.showStoredMaterials ?? true;
        this.showItems =
            (data as { clientPreview?: OwnerInvoiceClientPreview }).clientPreview?.showItems ??
            true;
        this.showInvoiceDescription =
            data.customerInvoiceInformation?.showInvoiceDescription ?? false;
        this.groupLineItemsByCostCode =
            data.customerInvoiceInformation?.groupLineItemsByCostCode ?? false;
        this.hideCostCodes =
            (data as { customerInvoiceInformation?: { hideCostCodes?: boolean } })
                .customerInvoiceInformation?.hideCostCodes ?? false;
        this.hideTaxAgencyDetail =
            (data as { customerInvoiceInformation?: { hideTaxAgencyDetail?: boolean } })
                .customerInvoiceInformation?.hideTaxAgencyDetail ?? false;

        this.jobHasLoanPaymentMethod = data.jobHasLoanPaymentMethod ?? false;
        this.loanHasPendingDrawRequest = data.loanHasPendingDrawRequest ?? false;
        this.builderHasLendingEnabled = data.builderHasLendingEnabled ?? false;
        this.isConnectedToInboundPayments = data.isConnectedToInboundPayments ?? false;
        this.isInboundPaymentsEnabledOnJob = data.isInboundPaymentsEnabledOnJob ?? false;
        this.disableApplyADepositButton = data.disableApplyADepositButton ?? false;
        this.invoiceDate = data.invoiceDate ? moment(data.invoiceDate) : undefined;

        if (data.viewedByOwnerOn) {
            this.viewedByOwnerOn = moment(data.viewedByOwnerOn);
        }
        this.effectiveTaxVersion = data.effectiveTaxVersion ?? JobTaxVersion.None;
        this.invoiceFormat = data.invoiceFormat ?? InvoiceFormat.Invoice;
        this.lineItemGroupStrategy = data.lineItemGroupStrategy;
        this.estimateHasAllowances = data.estimateHasAllowances ?? false;
        const clientPreview = (data as { clientPreview?: OwnerInvoiceClientPreview }).clientPreview;
        this.printHeader = clientPreview?.printHeader
            ? new PrintHeaderAPI(
                  clientPreview.printHeader as ConstructorParameters<typeof PrintHeaderAPI>[0],
              )
            : undefined;
        this.previewJobsite = clientPreview?.previewJobsite ?? undefined;
        this.qrCodeUrl = clientPreview?.qrCodeUrl ?? null;
        this.paymentCode = clientPreview?.paymentCode ?? null;
        this.showPaymentFromPrintoutInformation =
            clientPreview?.showPaymentFromPrintoutInformation ?? false;
        this.canShowPaymentFromPrintoutInformation =
            clientPreview?.canShowPaymentFromPrintoutInformation ?? false;
        this.showFees = clientPreview?.showFees ?? false;
        this.showMerchantPayments = clientPreview?.showMerchantPayments ?? false;
        this.hasCustomFields = clientPreview?.hasCustomFields ?? false;
        this.customFieldsPrint = (clientPreview?.customFieldsPrint ?? []).map(
            (cf) => new CustomFieldPrintItem(cf),
        );
        this.originalContractSum = clientPreview?.originalContractSum ?? 0;
        this.netChangeOrderTotal = clientPreview?.netChangeOrderTotal ?? 0;
        this.hideLaborCostAndMarkup = clientPreview?.hideLaborCostAndMarkup ?? false;
        this.hideBuilderMarkupFromSubtotal =
            clientPreview?.hideBuilderMarkupFromSubtotal ??
            (data as { customerInvoiceInformation?: { hideBuilderMarkupFromSubtotal?: boolean } })
                .customerInvoiceInformation?.hideBuilderMarkupFromSubtotal ??
            false;
    }
    printHeader?: PrintHeaderAPI;
    previewJobsite?: OwnerInvoicePreviewJobsite;
    qrCodeUrl: string | null;
    paymentCode: string | null;
    showPaymentFromPrintoutInformation: boolean;
    canShowPaymentFromPrintoutInformation: boolean;
    showFees: boolean;
    showMerchantPayments: boolean;
    hasCustomFields: boolean;
    customFieldsPrint: CustomFieldPrintItem[];
    originalContractSum: number;
    netChangeOrderTotal: number;

    id: number;
    jobId: number;
    billRateEnabledForJob: boolean;
    builderId: number;
    externalId?: string;
    externalBuilderId?: string;
    externalJobId?: string;
    instanceKey: string;
    jobContractType?: JobContractType;
    invoiceFormat: InvoiceFormat;
    isSelectionsReimaginedJob?: boolean;

    jobName: string;
    customInvoiceId: string;
    invoiceJobPrefixes?: string;
    title: string;
    recentItemsTooltip: RecentItemsTooltipConfig;
    merchantPayments?: MerchantPaymentListData;
    invoicePayments?: InvoicePayment[];

    amountPaid: number;
    balance: number;
    paidDate?: moment.Moment;
    amount: number;

    description: string;
    closingText: string;
    internalNotes: string;
    addedBy: string;
    status: InvoiceStatus;
    releasedDate?: moment.Moment;
    releasedByName?: string;
    scheduledReleaseDate?: moment.Moment;

    createdByDate: moment.Moment;
    createdByName: string;
    createdById: number | null;
    lastUpdatedByDate: moment.Moment;
    lastUpdatedByName: string;
    lastUpdatedById: number | null;
    viewedByOwnerOn: moment.Moment;

    isTemplate: boolean;
    canAdd: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canVoid: boolean;
    hasNewerProgressInvoice: boolean;
    canRefund: boolean;
    canPartiallyRefund: boolean;
    canPayOtherAmount: boolean;
    showRefund: boolean;
    showDeleteMessage: boolean;
    shouldRequestOwnerEmail: boolean;
    shouldPromptNotifyOwner: boolean;
    canOwnerViewInvoicesTab: boolean;
    ownerEmail: Email;

    isConnectedToAccounting: boolean;
    isConnectedAndInvoicedToAccounting: boolean;
    isJobAccountingConnected: boolean;
    isBilled?: boolean;
    showPayOnlineBtn: boolean;
    showPushPaymentBtn: boolean;
    showTerminalPaymentsEnabled: boolean;
    payOnlineUrl: string;
    payOnlineMessage: string;
    addFromRelatedEntities: AddFromRelatedEntitiesResponse;
    paymentLimitAmount: number | null;
    maxTransactionAmount: number;
    // IHasLinkedScheduleItem
    linkToScheduleItemEntity: LinkToScheduleItemEntity;

    // IHasAttachedFiles
    attachedFiles: AttachedFiles;
    attachedFilesForRelatedEntity: FilesFromExternalUrlsResponse | BTFileSystem[];
    clientBillLinkIds: number[];
    jobSharesBillsWithClients?: boolean;
    documentInstanceType: DocumentInstanceType = DocumentInstanceType.CustomerInvoices;
    name: string = "Invoice";

    // IHasDiscussionContainer
    discussions: DiscussionsEntity;

    // IHasCustomFields
    customFields: ICustomFieldResponse[];
    customFieldAssociatedType: CustomFieldAssociatedType = CustomFieldAssociatedType.OwnerPayments;
    customFieldOptions: ICustomFieldOptionsList;
    customFieldsCanConfigure: boolean;

    // IHasLineItems
    lineItems?: IInvoiceLineItemData;
    costCategoryOptions?: Int32BTServiceListItemIEnumerableBTServiceValidatedField;
    lineItemContainerData: ILineItemContainerData;
    lineItemInitialValues: IInvoiceLineItemWithMetadata[];
    showLineItemsToOwner: boolean;
    groupLineItemsByCostCode: boolean;
    showRelatedStackItems: boolean;
    ownerInvoiceLineItems: IOwnerInvoiceLineItem[];
    hideLaborCostAndMarkup: boolean;
    hideBuilderMarkupFromSubtotal: boolean;
    hideCostCodes: boolean;
    hideTaxAgencyDetail: boolean;

    retainageProductServiceId: string | null;

    // IHasAccountingValidation
    accountingValidation: AccountingValidationData;
    accountingSectionTitle: string;
    invoiceAccountingLinkInformation?: IInvoiceAccountingLinkInformation;
    isLinkedToAccountingService: boolean;
    isAccountingSyncLocked: boolean;
    removeFromAccountingMessage?: string;
    resetToPendingCustomMessage: string | undefined;
    entityType: PaymentEntityTypes;

    shouldSendToAccounting: boolean | undefined;
    customTitle: string;

    totalTaxAmount?: number;
    accountingBalance?: number;
    accountingIntegrationName?: string;

    // Tax Fields
    taxMethod: BTSelectItem[] | undefined;
    taxGroupId?: number;
    jobDefaultTaxGroupId?: number | null;
    taxRateBreakdown?: TaxRateBreakdownInfo;
    totalWithTax?: number;

    hasCostCodesFeature: boolean;

    accountingIntegrationType?: AccountingIntegrationType;

    // Contact Information
    payeeList: IContactOrgLink[];
    hasBeenSentToOwner: boolean;

    encodedEntityId: string;
    taxAmount?: number;
    totalInvoiceAmountWithTaxes?: number;
    showDeadlineTime: boolean;
    deadLine?: moment.Moment;
    showInviteOnlinePaymentsButton: boolean;
    taxGroups?: BTSelectItem<TaxGroupServiceListItemExtraData>[];
    canViewAllInvoicesOnJob: boolean;
    applicableDeposits: ApplicableDeposit[] | null;

    columnPreferences: OwnerInvoiceLineItemColumn[];
    defaultCostPlusOwnerInvoiceColumns?: OwnerInvoiceLineItemColumn[];
    defaultGeneralOwnerInvoiceColumns?: OwnerInvoiceLineItemColumn[];
    defaultGeneralHideOwnerLineItems?: boolean;
    defaultGeneralGroupLineItemsByCostCode?: boolean;
    defaultCostPlusHideOwnerLineItems?: boolean;
    defaultCostPlusGroupLineItemsByCostCode?: boolean;
    defaultCostPlusHideLaborCostAndMarkup?: boolean;
    defaultCostPlusHideBuilderMarkupFromSubtotal?: boolean;
    defaultGeneralHideCostCodes?: boolean;
    defaultCostPlusHideCostCodes?: boolean;
    defaultGeneralHideTaxAgencyDetail?: boolean;
    defaultCostPlusHideTaxAgencyDetail?: boolean;
    showPaymentCode: boolean;
    showCustomFields: boolean;
    showCostCodes: boolean;
    showCategories: boolean;
    showContractorCertification: boolean;
    showArchitectCertification: boolean;
    showRetainage: boolean;
    showStoredMaterials: boolean;
    showItems: boolean;
    showInvoiceDescription: boolean;

    builderHasLendingEnabled: boolean;
    jobHasLoanPaymentMethod: boolean;
    loanHasPendingDrawRequest: boolean;
    isConnectedToInboundPayments: boolean;
    isInboundPaymentsEnabledOnJob: boolean;
    disableApplyADepositButton: boolean;
    invoiceDate?: moment.Moment;
    effectiveTaxVersion?: JobTaxVersion;
    lineItemGroupStrategy?: LineItemGroupStrategy;
    estimateHasAllowances?: boolean;

    getStatus = (): "unreleased" | null => {
        if (this.status === InvoiceStatus.Unreleased) {
            return "unreleased";
        }
        return null;
    };
}

export class InvoiceEntityNew implements IOwnerInvoiceEntityInterface {
    constructor(data: GetInvoiceResponse) {
        this.id = data.invoiceId ?? 0;
        this.jobId = data.jobId;
        this.billRateEnabledForJob = data.billRateEnabledForJob ?? false;
        this.builderId = data.builderId;
        this.externalId = data.externalId ?? undefined;
        this.externalBuilderId = data.externalBuilderId ?? undefined;
        this.externalJobId = data.externalJobId ?? undefined;
        this.jobName = data.jobName;
        this.invoiceFormat = data.invoiceFormat;
        this.instanceKey = uuidv4();
        this.customInvoiceId = data.customInvoiceId;
        this.invoiceJobPrefixes = data.invoiceJobPrefixes ?? "";
        this.title = data.title;
        if (data.recentItemsTooltip) {
            this.recentItemsTooltip = new RecentItemsTooltipConfig(data.recentItemsTooltip);
        }
        this.merchantPayments = data.merchantPayments
            ? new MerchantPaymentListData(data.merchantPayments)
            : undefined;
        this.invoicePayments = data.invoicePayments?.map(
            (payment: InvoicePaymentDetailsResponse) => new InvoicePayment(payment),
        );
        this.amountPaid = data.amountPaid ?? 0;
        this.balance = data.balance;
        this.amount = data.amount;
        this.canPayOtherAmount = data.canEditPaymentAmount ?? false;

        if (data.paidDate) {
            this.paidDate = moment(data.paidDate);
        }
        this.description = data.description ?? "";
        this.closingText = data.closingText ?? "";
        this.internalNotes = data.internalNotes ?? "";
        this.addedBy = data.addedBy ?? "";
        this.status = data.status.value ?? InvoiceStatus.Unreleased;

        this.createdByDate = moment(data.createdByDate);
        this.createdByName = data.createdByName;
        this.createdById = data.createdById ?? 0;
        this.lastUpdatedByDate = moment(data.lastUpdatedByDate);
        this.lastUpdatedByName = data.lastUpdatedByName;
        this.lastUpdatedById = data.lastUpdatedById ?? 0;

        this.isTemplate = data.isTemplate;
        this.canAdd = data.canAdd;
        this.canEdit = data.canEdit || this.id === 0;
        this.canDelete = data.canDelete;
        this.canVoid = data.canVoid;
        this.hasNewerProgressInvoice = data.hasNewerProgressInvoice;
        this.canRefund = data.canRefund ?? false;
        this.canPartiallyRefund = data.canPartiallyRefund ?? false;
        this.canPayOtherAmount = data.canEditPaymentAmount ?? false;
        this.showRefund = data.showRefund ?? false;
        this.showDeleteMessage = !!data.deleteMessage;
        this.shouldRequestOwnerEmail = data.shouldRequestOwnerEmail ?? false;
        // presence of this key on response indicates should prompt to notify
        this.shouldPromptNotifyOwner = data.showNotifyOwnerChkbox !== undefined;
        this.canOwnerViewInvoicesTab = data.canOwnerViewInvoicesTab;
        if (data.ownerEmail) {
            this.ownerEmail = new Email(data.ownerEmail);
        }
        this.jobContractType = data.jobContractType;
        this.isSelectionsReimaginedJob = data.isSelectionsReimaginedJob;
        this.isConnectedToAccounting = data.accountingInfo?.isConnected ?? false;
        this.isConnectedAndInvoicedToAccounting =
            data.accountingInfo?.isConnectedAndInvoiced ?? false;
        this.isJobAccountingConnected = data.isJobAccountingConnected ?? false;
        this.isBilled = data.accountingInfo?.isBilled ?? undefined;
        this.isLinkedToAccountingService =
            data.invoiceAccountingLinkInformation?.isInvoiceLinked === true;
        this.isAccountingSyncLocked =
            this.isLinkedToAccountingService &&
            (data.invoiceAccountingLinkInformation?.isInSyncingStatus ?? false);
        this.showPayOnlineBtn = data.showOnlinePayButton ?? false;
        this.showPushPaymentBtn = data.showPushPaymentBtn ?? false;
        this.showTerminalPaymentsEnabled = data.terminalPaymentsEnabled ?? false;
        this.payOnlineUrl = data.entityPayOnlineUrl ?? "";
        if (data.deadline) {
            this.linkToScheduleItemEntity = {
                ...mapDeadlineToScheduleLinkItem(data.deadline),
                paymentTerms: data.paymentTerms,
            };
        }
        this.attachedFiles = AttachedFiles.fromProposalForDisplayAttachedFilesBaseApiV2(
            data.attachedFiles,
        )!;
        if (data.attachedFilesForRelatedEntity) {
            this.attachedFilesForRelatedEntity = data.attachedFilesForRelatedEntity.map(
                (file) => new BTFileSystem(file),
            );
        }
        this.clientBillLinkIds = data.clientBillLinkIds ?? [];
        this.jobSharesBillsWithClients = data.jobSharesBillsWithClients ?? undefined;
        if (data.attachedFilesForRelatedEntityForNewInvoice) {
            this.attachedFilesForRelatedEntity = new FilesFromExternalUrlsResponse(
                data.attachedFilesForRelatedEntityForNewInvoice,
            );
        }
        this.paymentLimitAmount = data.paymentLimitAmount ?? null;
        this.maxTransactionAmount = data.maxTransactionAmount ?? 0;

        this.discussions = new DiscussionsEntity(data.discussions);

        this.customFields = (data.customFields?.customFields ??
            []) as unknown as ICustomFieldResponse[];
        this.customFieldOptions = (data.customFields?.customFieldOptions ??
            []) as unknown as ICustomFieldOptionsList;
        this.customFieldsCanConfigure = data.customFields?.customFieldsCanConfigure ?? false;
        this.showLineItemsToOwner = data.showLineItemsToOwner ?? false;
        this.showRelatedStackItems = data.showRelatedStackItems ?? false;
        this.hideLaborCostAndMarkup = data.clientPreview?.hideLaborCostAndMarkup ?? false;
        this.hideBuilderMarkupFromSubtotal =
            data.clientPreview?.hideBuilderMarkupFromSubtotal ?? false;

        if (data.lineItems) {
            this.lineItems = {
                defaults: data.lineItemDefaults as ILineItemDefaults,
                value: data.lineItems?.map((x: InvoiceLineItem) => {
                    // bt-line-item requires calculatedAmount to be populated
                    return { ...x, calculatedAmount: x?.builderCost };
                }) as ILineItemResponseWithMetaData[],
            };
        }
        this.costCategoryOptions = data.costCategoryOptions;

        this.lineItemContainerData = {
            entityId: this.id,
            pageType: "invoice",
            pageTypeEnum: LineItemType.OwnerInvoice,
            priceType: data.useLineItems ? PriceTypes.LineItems : PriceTypes.FlatFee,
            showToOwner: this.showLineItemsToOwner,
            showMarkupTotal: false,
            displayTitle: true,
            descriptionMax: 2500,
            internalNotesMax: 2500,
            hasEditPermission: this.canEdit,
            customUnitCostHeader: "Unit Price",
            customBuilderCostHeader: "Client Price",
            hideFlatFeeOwnerPrice: true,
            showToOwnerTooltipVisible: false,
            disableBuilderCost:
                this.isBilled ||
                this.isAccountingSyncLocked ||
                !this.canEdit ||
                this.status === InvoiceStatus.Void,
            descriptionReadOnly: this.isBilled || this.isAccountingSyncLocked,
            lineItemReadOnly:
                this.isBilled ||
                this.isAccountingSyncLocked ||
                !this.canEdit ||
                this.status === InvoiceStatus.Void,
            canReorderLineItems: true,
            canEditCostCodes: data.canEditCostCodes,
        };

        if (data.lineItems) {
            this.lineItemInitialValues = data.lineItems?.map(
                (x: InvoiceLineItem) =>
                    new LineItem(
                        x as ILineItemResponseWithMetaData,
                        this.lineItemContainerData.pageType,
                        this.lineItemContainerData.pageTypeEnum,
                    ),
            );
        } else {
            this.lineItemInitialValues = [];
        }

        this.retainageProductServiceId = data.retainageProductServiceId ?? null;

        this.ownerInvoiceLineItems =
            data.lineItems?.map((x: InvoiceLineItem) => new OwnerInvoiceLineItem({ ...x })) ?? [];
        if (data.accountingInfo) {
            this.removeFromAccountingMessage = data.accountingInfo.resetInvoiceMessage ?? "";
        }
        this.accountingValidation = {
            entityId: this.id,
            entityType: AccountingEntityTypes.OwnerInvoice,
            showValidation: false,
            showAccountsReceivable: data.accountingInfo?.showAccountsReceivable ?? false,
            canGetLatestStatus: data.accountingInfo?.canGetLatestStatus ?? false,
            canResetAccounting:
                this.removeFromAccountingMessage !== undefined &&
                this.removeFromAccountingMessage !== "",
            canSendToAccounting:
                data.accountingInfo?.canEditAccountingOnInvoice ||
                data.accountingInfo?.showCreateInvoiceButton,
            shouldValidateOnLoad: true,
            accountingIntegrationName: data.accountingInfo?.accountingIntegrationName ?? "",
            transactionsAlertMessage:
                data?.accountingInfo?.resetInvoiceTransactionsAlertMessage ?? "",
        };
        this.shouldSendToAccounting =
            this.canOwnerViewInvoicesTab &&
            (this.status === InvoiceStatus.Unreleased || this.status === InvoiceStatus.Scheduled) &&
            data.accountingInfo?.createInvoiceChkbox;
        this.accountingSectionTitle = data.accountingInfo?.accountingSectionTitle ?? "";

        if (
            this.status === InvoiceStatus.Void &&
            data.status?.actions?.find((x) => x.id === PaymentStatusType.PendingReleased)
        ) {
            this.resetToPendingCustomMessage =
                data.status?.actions?.find((x) => x.id === PaymentStatusType.PendingReleased)
                    ?.message ?? "";
        }
        this.entityType = PaymentEntityTypes.OwnerInvoice;
        this.payOnlineMessage = data.payOnlineMessage ?? "";
        this.customTitle = `Invoice ID ${this.customInvoiceId}: ${this.title}`;

        this.totalTaxAmount = data.accountingInfo?.totalTaxAmount;
        this.accountingBalance = data.accountingInfo?.accountingBalance;
        if (data.invoiceAccountingLinkInformation) {
            this.invoiceAccountingLinkInformation = {
                isInvoiceLinked: data.invoiceAccountingLinkInformation.isInvoiceLinked ?? false,
                lastSyncDateUtc: data.invoiceAccountingLinkInformation.lastSyncDateUtc
                    ? moment(data.invoiceAccountingLinkInformation.lastSyncDateUtc)
                    : null,
                integrationType: data.invoiceAccountingLinkInformation.integrationType,
                isInSyncingStatus: data.invoiceAccountingLinkInformation.isInSyncingStatus ?? false,
            };
        }
        this.accountingIntegrationName =
            data?.accountingInfo?.accountingIntegrationName ?? undefined;
        this.addFromRelatedEntities = {
            showRelatedChangeOrders: data.addFromRelatedEntities?.showRelatedChangeOrders ?? false,
            showRelatedSelectionChoices:
                data.addFromRelatedEntities?.showRelatedSelectionChoices ?? false,
            showRelatedEstimates: data.addFromRelatedEntities?.showRelatedEstimates ?? false,
            showRelatedBillsAndPurchaseOrderPayments:
                data.addFromRelatedEntities?.showRelatedBillsAndPurchaseOrderPayments ?? false,
            showRelatedTimeClockItems:
                data.addFromRelatedEntities?.showRelatedTimeClockItems ?? false,
            showRelatedAccountingCosts:
                data.addFromRelatedEntities?.showRelatedAccountingCosts ?? false,
        };

        this.taxMethod =
            data.taxInformation?.taxMethod &&
            mapBTServiceDropdownToSelectItem(data.taxInformation.taxMethod);
        this.taxGroupId = data.taxInformation?.taxGroupId ?? NoTaxId;
        this.jobDefaultTaxGroupId = data.taxInformation?.jobDefaultTaxGroupId ?? null;
        this.taxRateBreakdown =
            data.taxInformation?.taxRateBreakdown &&
            new TaxRateBreakdownInfo({
                taxRateBreakdownItems:
                    data.taxInformation?.taxRateBreakdown?.taxRateBreakdownItems?.map((x) => {
                        return {
                            id: x.id ?? 0,
                            name: x.name ?? "",
                            percentage: x.percentage ?? 0,
                            isActive: x.isActive ?? false,
                            applicableAmount: x.applicableAmount ?? 0,
                            taxAmount: x.taxAmount ?? 0,
                        };
                    }) ?? [],

                totalTax: data.taxInformation?.taxRateBreakdown?.totalTax ?? 0,
                taxOverride: data.taxInformation?.taxRateBreakdown?.taxOverride ?? 0,
            });
        this.totalWithTax = data.totalWithTax;
        this.hasCostCodesFeature = data.hasCostCodesFeature ?? false;

        this.accountingIntegrationType =
            data.accountingInfo?.accountingType.valueOf() ?? AccountingIntegrationType.None;
        this.payeeList =
            data.payeeList?.map((x: UserOrgLink) => new ContactOrgLink(x as IContactOrgLink)) ?? [];
        this.hasBeenSentToOwner = data.hasBeenSentToOwner ?? false;

        // not used
        // this.encodedEntityId = data.encodedEntityId;

        // only populated on client portal
        this.taxAmount = data.accountingInfo?.taxAmount ?? undefined;
        this.totalInvoiceAmountWithTaxes =
            data.accountingInfo?.totalInvoiceAmountWithTaxes ?? undefined;
        this.showDeadlineTime =
            data.deadline?.dueTime !== undefined && data.deadline?.dueTime !== null;
        if (data.deadline?.dueTime) {
            this.deadLine = moment(data.deadline.dueTime);
        } else if (data.deadline?.dueDate) {
            this.deadLine = moment(data.deadline.dueDate);
        }

        this.showInviteOnlinePaymentsButton = data.showInvitePayOnlineButton ?? false;
        this.taxGroups =
            data.lineItemDefaults &&
            getTaxGroupSelectItemsFromLineItemDefaults(data.lineItemDefaults as ILineItemDefaults);

        this.canViewAllInvoicesOnJob = data.canViewAllInvoicesOnJob ?? false;
        this.applicableDeposits =
            data.applicableDeposits?.map((x) => new ApplicableDeposit(x)) ?? [];
        if (data.releasedDate) {
            this.releasedDate = moment(data.releasedDate);
        }
        this.releasedByName = data.releasedByName ?? "";

        if (data.scheduledReleaseDate) {
            this.scheduledReleaseDate = moment(data.scheduledReleaseDate);
        }

        this.columnPreferences =
            data.ownerPreviewDisplayPreferences?.ownerInvoiceLineItemColumnPreferences ?? [];
        this.defaultGeneralOwnerInvoiceColumns =
            data.ownerPreviewDisplayPreferences?.defaultGeneralOwnerInvoiceColumns ?? [];
        this.defaultCostPlusOwnerInvoiceColumns =
            data.ownerPreviewDisplayPreferences?.defaultCostPlusOwnerInvoiceColumns ?? [];
        this.defaultGeneralHideOwnerLineItems =
            data.ownerPreviewDisplayPreferences?.defaultGeneralHideOwnerLineItems ?? false;
        this.defaultCostPlusHideOwnerLineItems =
            data.ownerPreviewDisplayPreferences?.defaultCostPlusHideOwnerLineItems ?? false;
        this.defaultGeneralGroupLineItemsByCostCode =
            data.ownerPreviewDisplayPreferences?.defaultGeneralGroupLineItemsByCostCode ?? false;
        this.defaultCostPlusGroupLineItemsByCostCode =
            data.ownerPreviewDisplayPreferences?.defaultCostPlusGroupLineItemsByCostCode ?? false;
        this.defaultCostPlusHideLaborCostAndMarkup =
            data.ownerPreviewDisplayPreferences?.defaultCostPlusHideLaborCostAndMarkup ?? false;
        this.defaultCostPlusHideBuilderMarkupFromSubtotal =
            data.ownerPreviewDisplayPreferences?.defaultCostPlusHideBuilderMarkupFromSubtotal ??
            false;
        this.defaultGeneralHideCostCodes =
            data.ownerPreviewDisplayPreferences?.defaultGeneralHideCostCodes ?? false;
        this.defaultCostPlusHideCostCodes =
            data.ownerPreviewDisplayPreferences?.defaultCostPlusHideCostCodes ?? false;
        this.defaultGeneralHideTaxAgencyDetail =
            data.ownerPreviewDisplayPreferences?.defaultGeneralHideTaxAgencyDetail ?? false;
        this.defaultCostPlusHideTaxAgencyDetail =
            data.ownerPreviewDisplayPreferences?.defaultCostPlusHideTaxAgencyDetail ?? false;
        this.showPaymentCode = data.ownerPreviewDisplayPreferences?.showPaymentCode ?? false;
        this.showCustomFields = data.ownerPreviewDisplayPreferences?.showCustomFields ?? false;
        this.showCostCodes = data.clientPreview?.showCostCodes ?? true;
        this.showCategories = data.clientPreview?.showCategories ?? true;
        this.showContractorCertification = data.clientPreview?.showContractorCertification ?? true;
        this.showArchitectCertification = data.clientPreview?.showArchitectCertification ?? true;
        this.showRetainage = data.clientPreview?.showRetainage ?? true;
        this.showStoredMaterials = data.clientPreview?.showStoredMaterials ?? true;
        this.showItems = data.clientPreview?.showItems ?? true;
        this.showInvoiceDescription =
            data.ownerPreviewDisplayPreferences?.showInvoiceDescription ?? false;
        this.groupLineItemsByCostCode =
            data.ownerPreviewDisplayPreferences?.groupLineItemsByCostCode ?? false;
        this.hideCostCodes = data.ownerPreviewDisplayPreferences?.hideCostCodes ?? false;
        this.hideTaxAgencyDetail =
            data.ownerPreviewDisplayPreferences?.hideTaxAgencyDetail ?? false;

        this.jobHasLoanPaymentMethod = data.jobHasLoanPaymentMethod ?? false;
        this.loanHasPendingDrawRequest = data.loanHasPendingDrawRequest ?? false;
        this.builderHasLendingEnabled = data.builderHasLendingEnabled ?? false;
        this.isConnectedToInboundPayments = data.isConnectedToInboundPayments ?? false;
        this.isInboundPaymentsEnabledOnJob = data.isInboundPaymentsEnabledOnJob ?? false;
        this.disableApplyADepositButton = data.disableApplyADepositButton ?? false;

        if (data.viewedByOwnerOn) {
            this.viewedByOwnerOn = moment(data.viewedByOwnerOn);
        }
        this.invoiceDate = data.invoiceDate ? moment(data.invoiceDate) : undefined;
        this.effectiveTaxVersion = data.effectiveTaxVersion;
        this.lineItemGroupStrategy = data.lineItemGroupStrategy ?? undefined;
        this.estimateHasAllowances = data.estimateHasAllowances ?? false;
        const clientPreview = (data as { clientPreview?: OwnerInvoiceClientPreview }).clientPreview;
        this.printHeader = clientPreview?.printHeader
            ? new PrintHeaderAPI(
                  clientPreview.printHeader as ConstructorParameters<typeof PrintHeaderAPI>[0],
              )
            : undefined;
        this.previewJobsite = clientPreview?.previewJobsite ?? undefined;
        this.qrCodeUrl = clientPreview?.qrCodeUrl ?? null;
        this.paymentCode = clientPreview?.paymentCode ?? null;
        this.showPaymentFromPrintoutInformation =
            clientPreview?.showPaymentFromPrintoutInformation ?? false;
        this.canShowPaymentFromPrintoutInformation =
            clientPreview?.canShowPaymentFromPrintoutInformation ?? false;
        this.showFees = clientPreview?.showFees ?? false;
        this.showMerchantPayments = clientPreview?.showMerchantPayments ?? false;
        this.hasCustomFields = clientPreview?.hasCustomFields ?? false;
        this.customFieldsPrint = (clientPreview?.customFieldsPrint ?? []).map(
            (cf) => new CustomFieldPrintItem(cf),
        );
        this.originalContractSum = clientPreview?.originalContractSum ?? 0;
        this.netChangeOrderTotal = clientPreview?.netChangeOrderTotal ?? 0;
    }
    printHeader?: PrintHeaderAPI;
    previewJobsite?: OwnerInvoicePreviewJobsite;
    qrCodeUrl: string | null;
    paymentCode: string | null;
    showPaymentFromPrintoutInformation: boolean;
    canShowPaymentFromPrintoutInformation: boolean;
    showFees: boolean;
    showMerchantPayments: boolean;
    hasCustomFields: boolean;
    customFieldsPrint: CustomFieldPrintItem[];
    originalContractSum: number;
    netChangeOrderTotal: number;
    id: number;
    jobId: number;
    billRateEnabledForJob: boolean;
    builderId: number;
    externalId?: string;
    externalBuilderId?: string;
    externalJobId?: string;
    instanceKey: string;
    jobContractType?: JobContractType;
    isSelectionsReimaginedJob?: boolean;

    jobName: string;
    invoiceFormat: InvoiceFormat;
    customInvoiceId: string;
    invoiceJobPrefixes?: string;
    title: string;
    recentItemsTooltip: RecentItemsTooltipConfig;
    merchantPayments?: MerchantPaymentListData;
    invoicePayments?: InvoicePayment[];

    amountPaid: number;
    balance: number;
    paidDate?: moment.Moment;
    amount: number;

    description: string;
    closingText: string;
    internalNotes: string;
    addedBy: string;
    status: InvoiceStatus;
    releasedDate?: moment.Moment;
    releasedByName?: string;
    scheduledReleaseDate?: moment.Moment;

    createdByDate: moment.Moment;
    createdByName: string;
    createdById: number | null;
    lastUpdatedByDate: moment.Moment;
    lastUpdatedByName: string;
    lastUpdatedById: number | null;
    viewedByOwnerOn: moment.Moment;

    isTemplate: boolean;
    canAdd: boolean;
    canEdit: boolean;
    canDelete: boolean;
    canVoid: boolean;
    hasNewerProgressInvoice: boolean;
    canRefund: boolean;
    canPartiallyRefund: boolean;
    canPayOtherAmount: boolean;
    showRefund: boolean;
    showDeleteMessage: boolean;
    shouldRequestOwnerEmail: boolean;
    shouldPromptNotifyOwner: boolean;
    canOwnerViewInvoicesTab: boolean;
    ownerEmail: Email;

    isConnectedToAccounting: boolean;
    isConnectedAndInvoicedToAccounting: boolean;
    isJobAccountingConnected: boolean;
    isBilled?: boolean;
    showPayOnlineBtn: boolean;
    showPushPaymentBtn: boolean;
    showTerminalPaymentsEnabled: boolean;
    payOnlineUrl: string;
    payOnlineMessage: string;
    addFromRelatedEntities: AddFromRelatedEntitiesResponse;
    paymentLimitAmount: number | null;
    maxTransactionAmount: number;
    // IHasLinkedScheduleItem
    linkToScheduleItemEntity: LinkToScheduleItemEntity;

    // IHasAttachedFiles
    attachedFiles: AttachedFiles;
    attachedFilesForRelatedEntity: FilesFromExternalUrlsResponse | BTFileSystem[];
    clientBillLinkIds: number[];
    jobSharesBillsWithClients?: boolean;
    documentInstanceType: DocumentInstanceType = DocumentInstanceType.CustomerInvoices;
    name: string = "Invoice";

    // IHasDiscussionContainer
    discussions: DiscussionsEntity;

    // IHasCustomFields
    customFields: ICustomFieldResponse[];
    customFieldAssociatedType: CustomFieldAssociatedType = CustomFieldAssociatedType.OwnerPayments;
    customFieldOptions: ICustomFieldOptionsList;
    customFieldsCanConfigure: boolean;

    // IHasLineItems
    lineItems?: IInvoiceLineItemData;
    costCategoryOptions?: Int32BTServiceListItemIEnumerableBTServiceValidatedField;
    lineItemContainerData: ILineItemContainerData;
    lineItemInitialValues: LineItem[];
    showLineItemsToOwner: boolean;
    groupLineItemsByCostCode: boolean;
    showRelatedStackItems: boolean;
    ownerInvoiceLineItems: IOwnerInvoiceLineItem[];

    retainageProductServiceId: string | null;

    // IHasAccountingValidation
    accountingValidation: AccountingValidationData;
    accountingSectionTitle: string;
    invoiceAccountingLinkInformation?: IInvoiceAccountingLinkInformation;
    isLinkedToAccountingService: boolean;
    isAccountingSyncLocked: boolean;
    removeFromAccountingMessage?: string;
    resetToPendingCustomMessage: string | undefined;
    entityType: PaymentEntityTypes;

    shouldSendToAccounting: boolean | undefined;
    customTitle: string;

    totalTaxAmount?: number;
    accountingBalance?: number;
    accountingIntegrationName?: string;

    // Tax Fields
    taxMethod: BTSelectItem[] | undefined;
    taxGroupId?: number;
    jobDefaultTaxGroupId?: number | null;
    taxRateBreakdown?: TaxRateBreakdownInfo;
    totalWithTax?: number;

    hasCostCodesFeature: boolean;

    accountingIntegrationType?: AccountingIntegrationType;

    // Contact Information
    payeeList: IContactOrgLink[];
    hasBeenSentToOwner: boolean;

    encodedEntityId: string;
    taxAmount?: number;
    totalInvoiceAmountWithTaxes?: number;
    showDeadlineTime: boolean;
    deadLine?: moment.Moment;
    showInviteOnlinePaymentsButton: boolean;
    taxGroups?: BTSelectItem<TaxGroupServiceListItemExtraData>[];
    canViewAllInvoicesOnJob: boolean;
    applicableDeposits: ApplicableDeposit[] | null;

    columnPreferences: OwnerInvoiceLineItemColumn[];
    defaultCostPlusOwnerInvoiceColumns?: OwnerInvoiceLineItemColumn[];
    defaultGeneralOwnerInvoiceColumns?: OwnerInvoiceLineItemColumn[];
    defaultGeneralHideOwnerLineItems?: boolean;
    defaultGeneralGroupLineItemsByCostCode?: boolean;
    defaultCostPlusHideOwnerLineItems?: boolean;
    defaultCostPlusGroupLineItemsByCostCode?: boolean;
    defaultCostPlusHideLaborCostAndMarkup?: boolean;
    defaultCostPlusHideBuilderMarkupFromSubtotal?: boolean;
    defaultGeneralHideCostCodes?: boolean;
    defaultCostPlusHideCostCodes?: boolean;
    defaultGeneralHideTaxAgencyDetail?: boolean;
    defaultCostPlusHideTaxAgencyDetail?: boolean;
    showPaymentCode: boolean;
    showCustomFields: boolean;
    showCostCodes: boolean;
    showCategories: boolean;
    showContractorCertification: boolean;
    showArchitectCertification: boolean;
    showRetainage: boolean;
    showStoredMaterials: boolean;
    showItems: boolean;
    showInvoiceDescription: boolean;

    builderHasLendingEnabled: boolean;
    jobHasLoanPaymentMethod: boolean;
    loanHasPendingDrawRequest: boolean;
    isConnectedToInboundPayments: boolean;
    isInboundPaymentsEnabledOnJob: boolean;
    disableApplyADepositButton: boolean;
    invoiceDate?: moment.Moment;
    effectiveTaxVersion?: JobTaxVersion | undefined;
    lineItemGroupStrategy?: LineItemGroupStrategy;
    estimateHasAllowances?: boolean;
    hideLaborCostAndMarkup: boolean;
    hideBuilderMarkupFromSubtotal: boolean;
    hideCostCodes: boolean;
    hideTaxAgencyDetail: boolean;

    getStatus = (): "unreleased" | null => {
        if (this.status === InvoiceStatus.Unreleased) {
            return "unreleased";
        }
        return null;
    };
}

export class OwnerInvoiceSaveResponse implements IWithServiceValidationErrors {
    constructor(data: any) {
        this.id = data.id;
        this.formMessage = data.formMessage;
        this.failedFields = data.failedFields;
        this.invoiceAccountingError = data.invoiceAccountingError;
        this.relatedEntitySyncErrorMessage = data.relatedEntitySyncErrorMessage;

        if (data.accountingValidation) {
            this.accountingValidation = new AccountingValidationResponse(data.accountingValidation);
        }
    }
    id: number;
    jobId: number;
    formMessage: string;
    failedFields: ServiceValidation[];
    invoiceAccountingError?: string;
    accountingValidation?: AccountingValidationResponse;
    relatedEntitySyncErrorMessage?: string;
}

export class OwnerInvoiceAccountingResponse {
    constructor(data: any) {
        this.id = data.id;
        this.accountingValidation = data.accountingValidation;
    }

    id: number;
    accountingValidation?: AccountingValidationResponse;
}

export class InvoicePayment {
    constructor(data: any) {
        this.id = data.invoicePaymentId;
        this.paymentDate = moment(data.paymentDate);
        this.paymentAmount = data.paymentAmount.value;
        this.paymentMethod = data.paymentMethod;
        this.paymentStatus = data.paymentStatus;
        this.paymentStatusReason = data.paymentStatusReason;
        this.isLinkedToAccounting = data.isLinkedToAccounting;
        this.isOnlinePayment = data.isOnlinePayment;
        this.lastUpdatedDate = data.lastUpdatedDate && moment(data.lastUpdatedDate);
        this.createdBy = data.createdBy;
        this.createdById = data.createdById;
        this.createdByDate = moment(data.createdByDate);
        this.receivedByName = data.receivedByName;
        this.voidedDate = data.voidedDate && moment(data.voidedDate);
        this.voidedByName = data.voidedBy;
        this.merchantPaymentMethod = data.merchantPaymentMethod;
        this.netDeposit = data.netDeposit?.value;
        this.feeAmount = data.feeAmount?.value;
        this.merchantPaymentId = data.merchantPaymentId;
        this.unverifiedPaymentMethod = data.unverifiedPaymentMethod;
        this.originalMerchantPaymentAmount = data.originalMerchantPaymentAmount?.value;
        this.isPendingSync = data.isPendingSync;
        this.isStuckInPendingSync = data.isStuckInPendingSync;
        this.refundReason = data.refundReason;
        this.showOwnerRefundReason = data.showOwnerRefundReason;
        this.canVoid = data.canVoid;
        this.creditMemos = data.creditMemos
            ? data.creditMemos.map((cm: any) => new CreditMemoAppliedInvoice(cm))
            : null;
        this.isDeposit = this.paymentMethod === InvoicePaymentMethods.Deposit;
        this.depositId = data.depositId;
        this.depositTitle = data.depositTitle;
        this.creditMemoId = data.creditMemoId;
        this.creditMemoTitle = data.creditMemoTitle;
        this.hasChildTransaction = data.hasChildTransaction;
        this.viewedByOwnerOn = data.viewedByOwnerOn && moment(data.viewedByOwnerOn);
        const linkInfo = (data as InvoicePaymentDetailsResponse).paymentAccountingLinkInformation;
        if (linkInfo) {
            this.paymentAccountingLinkInformation = {
                isPaymentLinked: linkInfo.isPaymentLinked ?? false,
                lastSyncDateUtc: linkInfo.lastSyncDateUtc ? moment(linkInfo.lastSyncDateUtc) : null,
                integrationType: linkInfo.integrationType,
            };
        }
    }

    id: number;
    paymentDate: moment.Moment;
    paymentAmount: number;
    paymentMethod: InvoicePaymentMethods;
    paymentStatus: CustomerInvoicePaymentStatus;
    paymentStatusReason: string;
    isLinkedToAccounting: boolean;
    isOnlinePayment: boolean;
    lastUpdatedDate?: moment.Moment;
    createdBy: string;
    createdById: number;
    createdByDate: moment.Moment;
    receivedByName: string;
    voidedByName: string;
    voidedDate: moment.Moment;
    merchantPaymentMethod?: PaymentType;
    unverifiedPaymentMethod?: UnverfiedPaymentMethodResponse;
    netDeposit?: number;
    feeAmount?: number;
    merchantPaymentId?: number;
    originalMerchantPaymentAmount?: number;
    isPendingSync: boolean;
    isStuckInPendingSync: boolean;
    refundReason: string;
    showOwnerRefundReason: boolean;
    canVoid: boolean;
    creditMemos: CreditMemoAppliedInvoice[] | null;
    isDeposit?: boolean;
    depositId?: number;
    depositTitle?: string;
    creditMemoId?: number;
    creditMemoTitle?: string;
    hasChildTransaction: boolean;
    viewedByOwnerOn: moment.Moment;
    paymentAccountingLinkInformation?: IPaymentAccountingLinkInformation;

    isOnlinePaymentAmountChanged() {
        return (
            this.isOnlinePayment &&
            this.originalMerchantPaymentAmount !== undefined &&
            this.originalMerchantPaymentAmount !== this.paymentAmount
        );
    }
}

export class UnstackLineItemRequest {
    constructor(
        lineItemIds: number[],
        newLineItemId: number,
        lineItemType: LineItemType,
        jobId: number,
        markupPercent?: number,
    ) {
        this.lineItemIds = lineItemIds;
        this.newLineItemId = newLineItemId;
        this.lineItemType = lineItemType;
        this.jobId = jobId;
        this.markupPercent = markupPercent;
    }
    lineItemIds: number[];
    newLineItemId: number;
    lineItemType: LineItemType;
    jobId: number;
    markupPercent?: number;
}

export class UnstackLineItemResponse {
    constructor(data: any) {
        this.lineItems = data.lineItems.map(
            (li: InvoiceLineItem) => new OwnerInvoiceLineItem({ ...li }),
        );
        this.newLineItemId = data.newLineItemId;
    }
    lineItems: OwnerInvoiceLineItem[];
    newLineItemId: number;
}

export class ApplicableDeposit {
    constructor(
        data: (typeof OwnerInvoiceApiResponse.applicableDeposits)[0] | ApplicableDepositResponse,
    ) {
        this.id = data.id ?? 0;
        this.referenceId = data.referenceId ?? "";
        this.title = data.title ?? "";
        this.amountRemaining = data.amountRemaining ?? 0;
        this.isConnectedToAccounting = data.isConnectedToAccounting ?? false;
    }

    id: number;
    referenceId: string;
    title: string;
    amountRemaining: number;
    isConnectedToAccounting: boolean;
}

export function getInvoicePaymentMethodText(invoicePayment: InvoicePayment) {
    switch (invoicePayment.paymentMethod) {
        case InvoicePaymentMethods.CreditCard:
            return "Credit Card";
        case InvoicePaymentMethods.Check:
            return "Check";
        case InvoicePaymentMethods.OnlinePayment:
            return "Online Payment";
        case InvoicePaymentMethods.QuickBooks:
            return "QuickBooks";
        case InvoicePaymentMethods.Xero:
            return "Xero";
        case InvoicePaymentMethods.CreditMemo:
            return invoicePayment.creditMemoTitle
                ? `Credit memo - ${invoicePayment.creditMemoTitle}`
                : "Credit memo";
        case InvoicePaymentMethods.Deposit:
            return invoicePayment.depositTitle
                ? `Deposit - ${invoicePayment.depositTitle}`
                : "Deposit";
        case InvoicePaymentMethods.Wire:
            return "Wire";
        case InvoicePaymentMethods.SageIntacct:
            return "Sage Intacct";
        case InvoicePaymentMethods.Other:
            return "Other";
        default:
            return null;
    }
}

export function getPaymentTypeText(merchantPaymentMethod: PaymentType) {
    switch (merchantPaymentMethod) {
        case PaymentType.CreditCard:
            return "Credit Card";
        case PaymentType.Check:
            return "ACH";
        case PaymentType.Loan:
            return "Draw Request";
        case PaymentType.PushPayments:
            return "Bank Transfer";
        default:
            return null;
    }
}

export enum CustomerInvoicePaymentStatus {
    Paid = 0,
    Void = 1,
    Processing = 2,
    Failed = 3,
    Cancelled = 4,
    Reserved = 5,
    ChargedBack = 6,
    Complete = 7,
    Refunded = 8,
    Applied = 9,
    Declined = 10,
}

export class AddFromRelatedEntitiesResponse {
    showRelatedChangeOrders: boolean;
    showRelatedSelectionChoices: boolean;
    showRelatedEstimates: boolean;
    showRelatedBillsAndPurchaseOrderPayments: boolean;
    showRelatedTimeClockItems: boolean;
    showRelatedAccountingCosts: boolean;
}

export class LineItemColumnPreferencesSaveRequest {
    constructor(data: any) {
        this.preferences = data.preferences;
        this.saveAsDefault = data.saveAsDefault;
    }
    preferences: OwnerInvoiceLineItemColumn[];
    saveAsDefault: boolean;
}

export function getOwnerInvoiceItemColumnOptions(
    shouldAddTaxColumns: boolean,
    groupLineItems: boolean,
    canSelectRelatedItemColumns: boolean,
) {
    const columnOptions = [
        new BTSelectItem({
            id: OwnerInvoiceLineItemColumn.Items,
            name: "Items",
            value: OwnerInvoiceLineItemColumn.Items,
            disabled: true,
        }),
        new BTSelectItem({
            id: OwnerInvoiceLineItemColumn.CostType,
            name: "Cost type",
            value: OwnerInvoiceLineItemColumn.CostType,
            disabled: groupLineItems,
        }),
        new BTSelectItem({
            id: OwnerInvoiceLineItemColumn.Description,
            name: "Description",
            value: OwnerInvoiceLineItemColumn.Description,
            disabled: groupLineItems,
        }),
        new BTSelectItem({
            id: OwnerInvoiceLineItemColumn.UnitPrice,
            name: "Unit price",
            value: OwnerInvoiceLineItemColumn.UnitPrice,
            disabled: groupLineItems,
        }),
        new BTSelectItem({
            id: OwnerInvoiceLineItemColumn.Quantity,
            name: "Quantity",
            value: OwnerInvoiceLineItemColumn.Quantity,
            disabled: groupLineItems,
        }),
        new BTSelectItem({
            id: OwnerInvoiceLineItemColumn.BuilderCost,
            name: "Builder cost",
            value: OwnerInvoiceLineItemColumn.BuilderCost,
        }),
        new BTSelectItem({
            id: OwnerInvoiceLineItemColumn.OwnerPrice,
            name: "Client price",
            value: OwnerInvoiceLineItemColumn.OwnerPrice,
        }),
        new BTSelectItem({
            id: OwnerInvoiceLineItemColumn.UnitCost,
            name: "Unit cost",
            value: OwnerInvoiceLineItemColumn.UnitCost,
            disabled: groupLineItems,
        }),
        new BTSelectItem({
            id: OwnerInvoiceLineItemColumn.Markup,
            name: "Markup",
            value: OwnerInvoiceLineItemColumn.Markup,
            disabled: groupLineItems,
        }),
        new BTSelectItem({
            id: OwnerInvoiceLineItemColumn.MarkupAmount,
            name: "Markup amount",
            value: OwnerInvoiceLineItemColumn.MarkupAmount,
        }),
    ];

    if (shouldAddTaxColumns) {
        columnOptions.push(
            new BTSelectItem({
                id: OwnerInvoiceLineItemColumn.Tax,
                name: "Tax",
                value: OwnerInvoiceLineItemColumn.Tax,
                disabled: true,
            }),
        );
    }

    if (canSelectRelatedItemColumns) {
        columnOptions.push(
            new BTSelectItem({
                id: OwnerInvoiceLineItemColumn.RelatedItems,
                name: "Related items",
                value: OwnerInvoiceLineItemColumn.RelatedItems,
                disabled: groupLineItems,
            }),
            new BTSelectItem({
                id: OwnerInvoiceLineItemColumn.Files,
                name: "Files",
                value: OwnerInvoiceLineItemColumn.Files,
                disabled: groupLineItems,
            }),
        );
    }

    columnOptions.splice(
        2,
        0, // insert after Cost Type and don't remove any items
        new BTSelectItem({
            id: OwnerInvoiceLineItemColumn.MarkedAs,
            name: "Marked as",
            value: OwnerInvoiceLineItemColumn.MarkedAs,
            disabled: groupLineItems,
        }),
    );

    return columnOptions;
}

// Max character length for the rich-text invoice Description and Closing text fields
// (CKEditor entry + yup validation), shared across the invoice detail and settings pages.
export const INVOICE_RICH_TEXT_MAX_LENGTH = 200000;

export const ColumnsAllowedWithLineItemGrouping = [
    OwnerInvoiceLineItemColumn.BuilderCost,
    OwnerInvoiceLineItemColumn.MarkupAmount,
    OwnerInvoiceLineItemColumn.OwnerPrice,
    OwnerInvoiceLineItemColumn.Items,
    OwnerInvoiceLineItemColumn.Tax,
];

export function filterColumnsForCostCodeGrouping(
    columns: OwnerInvoiceLineItemColumn[],
    groupLineItems: boolean,
) {
    const validColumns = groupLineItems
        ? columns.filter((x) => ColumnsAllowedWithLineItemGrouping.includes(x))
        : columns;
    return validColumns;
}

export class OwnerInvoiceRelatedEntityIDsForAttachments {
    constructor(data: any) {
        this.relatedEntityIDs = getUniqueParents(data.lineItems).map((li) => li.id);
        this.lineItemType = data.lineItems[0].lineItemType;
    }
    relatedEntityIDs: (number | string)[] | null;
    lineItemType: LineItemType | null;
}
